import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { Check, X, Clock, ShieldAlert } from 'lucide-react';
export function PricingSection() {
  const scrollToContact = () => {
    document.querySelector('#contacto')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const comparisons = [{
    before: 'Buscar fotos por horas',
    after: 'Encuentras todo en segundos'
  }, {
    before: 'Armar informes manuales en Word',
    after: 'Generas reportes automáticos'
  }, {
    before: 'Decisiones perdidas en el chat',
    after: 'Historial permanente de decisiones'
  }, {
    before: 'Audios imposibles de revisar',
    after: 'Resúmenes y transcripción con IA'
  }, {
    before: 'Fotos sueltas sin contexto',
    after: 'Fotos ordenadas por obra, fecha y persona'
  }, {
    before: 'Información perdida si borran el grupo',
    after: 'Respaldo permanente online'
  }, {
    before: 'Capacitar al equipo en otra app',
    after: 'El equipo sigue usando WhatsApp'
  }];
  return <section id="precio" className="py-10 sm:py-12 bg-gray-50" data-id="element-195">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-196">
        <div className="text-center mb-12 sm:mb-16" data-id="element-197">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4" data-id="element-198">
            Precio fijo por obra. Sin cobrar por usuario.
          </h2>
          <p className="text-lg text-gray-600" data-id="element-199">
            Tu equipo completo puede participar sin preocuparse por licencias,
            usuarios o cupos.
          </p>
        </div>

        {/* Tiers */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-5 mb-16 items-stretch" data-id="element-200">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm text-center flex flex-col" data-id="element-201">
            <h3 className="text-lg font-bold text-gray-900 mb-1" data-id="element-202">Piloto</h3>
            <p className="text-sm text-gray-500 mb-4" data-id="element-203">
              Para probar con una obra real
            </p>
            <div className="text-3xl font-extrabold text-gray-900 mb-1" data-id="element-204">
              Gratis
            </div>
            <p className="text-xs text-gray-400" data-id="element-205">por 7 días</p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.05
        }} className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm text-center flex flex-col" data-id="element-206">
            <h3 className="text-lg font-bold text-gray-900 mb-1" data-id="element-207">Obra Chica</h3>
            <p className="text-sm text-gray-500 mb-4" data-id="element-208">Hasta 1.500 m²</p>
            <div className="text-3xl font-extrabold text-blue-600 mb-1" data-id="element-209">
              UF 15
            </div>
            <p className="text-xs text-gray-400" data-id="element-210">al mes</p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="bg-blue-600 rounded-2xl p-6 border border-blue-700 shadow-md text-center flex flex-col" data-id="element-211">
            <h3 className="text-lg font-bold text-white mb-1" data-id="element-212">Obra Mediana</h3>
            <p className="text-sm text-blue-100 mb-4" data-id="element-213">Hasta 5.000 m²</p>
            <div className="text-3xl font-extrabold text-white mb-1" data-id="element-214">UF 35</div>
            <p className="text-xs text-blue-200" data-id="element-215">al mes</p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.15
        }} className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm text-center flex flex-col" data-id="element-216">
            <h3 className="text-lg font-bold text-gray-900 mb-1" data-id="element-217">
              Obra Grande
            </h3>
            <p className="text-sm text-gray-500 mb-4" data-id="element-218">Más de 5.000 m²</p>
            <div className="text-3xl font-extrabold text-blue-600 mb-1" data-id="element-219">
              UF 80+
            </div>
            <p className="text-xs text-gray-400" data-id="element-220">al mes</p>
          </motion.div>
        </div>

        <p className="text-center text-sm text-gray-600 font-medium -mt-10 mb-12" data-id="element-221">
          Planes pagados incluyen{' '}
          <span className="text-gray-900 font-semibold" data-id="element-222">
            usuarios, mensajes y fotos sin límite razonable de uso.
          </span>
        </p>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden max-w-4xl mx-auto" data-id="element-223">
          {/* Column Headers */}
          <div className="grid grid-cols-2 bg-gray-50 border-b border-gray-200 p-4" data-id="element-224">
            <div className="text-center" data-id="element-225">
              <div className="text-[11px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider" data-id="element-226">
                Antes
              </div>
            </div>
            <div className="text-center" data-id="element-227">
              <div className="text-[11px] sm:text-xs font-bold text-blue-600 uppercase tracking-wider" data-id="element-228">
                Con Obra Digital
              </div>
            </div>
          </div>

          {/* Comparison Rows */}
          <div className="px-4" data-id="element-229">
            {comparisons.map((row, index) => <div key={index} className={`grid grid-cols-2 items-center py-3.5 gap-3 ${index < comparisons.length - 1 ? 'border-b border-gray-100' : ''}`} data-id="element-230">
                <div className="flex items-start gap-2" data-id="element-231">
                  <X className="w-4 h-4 text-gray-300 flex-shrink-0 mt-0.5" strokeWidth={2.5} data-id="element-232" />
                  <span className="text-[13px] sm:text-sm text-gray-500" data-id="element-233">
                    {row.before}
                  </span>
                </div>
                <div className="flex items-start gap-2" data-id="element-234">
                  <Check className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" strokeWidth={2.5} data-id="element-235" />
                  <span className="text-[13px] sm:text-sm text-gray-900 font-medium" data-id="element-236">
                    {row.after}
                  </span>
                </div>
              </div>)}
          </div>

          {/* CTA */}
          <div className="p-6 bg-gray-50 border-t border-gray-200" data-id="element-237">
            <button onClick={scrollToContact} className="w-full py-3.5 px-6 rounded-xl text-base font-bold text-white bg-blue-600 hover:bg-blue-700 transition-colors shadow-sm" data-id="element-238">
              Probar con una obra real
            </button>
          </div>
        </motion.div>

        {/* Value arguments */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.2
      }} className="mt-10 space-y-3 max-w-2xl mx-auto" data-id="element-239">
          <div className="flex items-center py-3.5 px-5 rounded-xl bg-white border border-gray-200 shadow-sm" data-id="element-240">
            <Clock className="w-4 h-4 mr-2.5 text-gray-400 flex-shrink-0" data-id="element-241" />
            <span className="text-gray-700 font-medium text-sm" data-id="element-242">
              3 horas semanales armando informes{' '}
              <span className="text-blue-600 font-bold" data-id="element-243">
                = más caro que Obra Digital
              </span>
            </span>
          </div>
          <div className="flex items-center py-3.5 px-5 rounded-xl bg-white border border-gray-200 shadow-sm" data-id="element-244">
            <ShieldAlert className="w-4 h-4 mr-2.5 text-gray-400 flex-shrink-0" data-id="element-245" />
            <span className="text-gray-700 font-medium text-sm" data-id="element-246">
              Una decisión perdida puede costar{' '}
              <span className="text-blue-600 font-bold" data-id="element-247">
                más que un año de respaldo
              </span>
            </span>
          </div>
          <p className="text-xs text-gray-500 text-center pt-3 font-semibold italic" data-id="element-248">
            Una obra no necesita más chats. Necesita memoria.
          </p>
        </motion.div>
      </div>
    </section>;
}